import { api } from "@/features/auth/api";

export type Rider = {
    id: string;
    email: string;
    full_name: string;
    phone: string;
    role: "rider";
    vehicle_type: string;
    vehicle_number: string;
    photo_url: string;
    zone: string;
    is_online: boolean;
    is_active: boolean;
    current_location: { lat: number; lng: number; updated_at: string } | null;
    rating: number;
    total_deliveries: number;
    total_earnings?: number;
    created_at: string | null;
};

export type DeliveryOrder = {
    id: string;
    number: string;
    total: number;
    item_count: number;
    items_preview: Array<{ name: string; quantity: number }>;
    status: string;
    payment: { method: string; status: string };
    address: {
        full_name: string;
        line1: string;
        line2: string;
        city: string;
        state: string;
        pincode: string;
        phone: string;
        landmark: string;
    };
    customer_note?: string;
    placed_at: string | null;
    eta: string | null;
    maps_url: string;
};

export type Delivery = {
    id: string;
    order_id: string;
    status:
        | "assigned"
        | "accepted"
        | "picked_up"
        | "out_for_delivery"
        | "delivered"
        | "rejected"
        | "reassigned";
    assigned_at: string | null;
    accepted_at: string | null;
    picked_up_at: string | null;
    delivered_at: string | null;
    earnings: number;
    otp: string;
    order?: DeliveryOrder;
    rider?: { id: string; full_name: string; phone: string };
};

export type EarningsSummary = {
    today: { earnings: number; deliveries: number };
    weekly: { earnings: number; deliveries: number };
    monthly: { earnings: number; deliveries: number };
    total: { earnings: number; deliveries: number };
    series: Array<{ date: string; earnings: number; deliveries: number }>;
};

export type OrderTracking = {
    order_id: string;
    number: string;
    status: string;
    eta: string | null;
    rider: {
        id: string;
        full_name: string;
        phone: string;
        photo_url: string;
        vehicle_type: string;
        vehicle_number: string;
        rating: number;
        current_location?: { lat: number; lng: number; updated_at: string } | null;
    } | null;
    progress: Array<{
        key: string;
        label: string;
        completed: boolean;
        at: string | null;
    }>;
    delivery: Delivery | null;
};

export const riderApi = {
    me: async (): Promise<Rider> => (await api.get("/rider/me")).data,
    toggleOnline: async (
        is_online: boolean,
        coords?: { lat: number; lng: number },
    ): Promise<Rider> =>
        (await api.post("/rider/online", { is_online, ...coords })).data,
    available: async (): Promise<Delivery[]> => (await api.get("/rider/available")).data,
    active: async (): Promise<Delivery[]> => (await api.get("/rider/active")).data,
    accept: async (id: string): Promise<Delivery> =>
        (await api.post(`/rider/deliveries/${id}/accept`)).data,
    reject: async (id: string): Promise<void> => {
        await api.post(`/rider/deliveries/${id}/reject`);
    },
    pickup: async (id: string): Promise<Delivery> =>
        (await api.post(`/rider/deliveries/${id}/pickup`)).data,
    outForDelivery: async (id: string): Promise<Delivery> =>
        (await api.post(`/rider/deliveries/${id}/out-for-delivery`)).data,
    verifyOtp: async (id: string, otp: string): Promise<Delivery> =>
        (await api.post(`/rider/deliveries/${id}/verify-otp`, { otp })).data,
    earnings: async (): Promise<EarningsSummary> => (await api.get("/rider/earnings")).data,
    history: async (): Promise<Delivery[]> => (await api.get("/rider/history")).data,
};

export const trackingApi = {
    order: async (id: string): Promise<OrderTracking> =>
        (await api.get(`/orders/${id}/tracking`)).data,
    live: async (id: string): Promise<TrackingLive> =>
        (await api.get(`/orders/${id}/tracking`)).data as unknown as TrackingLive,
    publicTrack: async (token: string): Promise<TrackingLive> =>
        (await api.get(`/track/${token}`)).data,
    createShare: async (id: string): Promise<{ token: string; url: string }> =>
        (await api.post(`/orders/${id}/share`)).data,
    chat: async (id: string): Promise<{ order_id: string; messages: ChatMessage[] }> =>
        (await api.get(`/orders/${id}/chat`)).data,
    postChat: async (id: string, text: string): Promise<ChatMessage> =>
        (await api.post(`/orders/${id}/chat`, { text })).data,
};

export type ChatMessage = {
    id: string;
    sender_id: string;
    sender_role: "customer" | "rider" | string;
    text: string;
    created_at: string | null;
};

export type TrackingLive = {
    order_id: string;
    number: string;
    status: string;
    eta: string | null;
    rider: OrderTracking["rider"];
    delivery: (Delivery & { path?: Array<{ lat: number; lng: number; at: string | null }> }) | null;
    customer_address?: {
        line1?: string;
        line2?: string;
        city?: string;
        pincode?: string;
        gps?: { lat: number; lng: number } | null;
    };
};

export const adminLiveApi = {
    riders: async (): Promise<{
        items: Array<{
            rider_id: string;
            full_name: string;
            phone: string;
            vehicle_type: string;
            current_location: { lat: number; lng: number; updated_at: string } | null;
            active_delivery: { id: string; number: string; status: string } | null;
            delivery_status: string | null;
        }>;
        generated_at: string;
    }> => (await api.get("/admin/riders/live")).data,
};

export const adminRiderApi = {
    list: async (params: { search?: string; online?: boolean; page?: number; page_size?: number } = {}) =>
        (await api.get<{ items: Rider[]; total: number; page: number; page_size: number }>(
            "/admin/riders",
            { params },
        )).data,
    create: async (payload: {
        full_name: string;
        email: string;
        phone: string;
        password: string;
        vehicle_type: string;
        vehicle_number: string;
        photo_url?: string;
        zone?: string;
    }): Promise<Rider> => (await api.post("/admin/riders", payload)).data,
    update: async (id: string, payload: Partial<Rider>): Promise<Rider> =>
        (await api.put(`/admin/riders/${id}`, payload)).data,
    remove: async (id: string): Promise<void> => {
        await api.delete(`/admin/riders/${id}`);
    },
    unassigned: async (): Promise<DeliveryOrder[]> =>
        (await api.get("/admin/deliveries/unassigned")).data,
    assign: async (order_id: string, rider_id: string): Promise<Delivery> =>
        (await api.post(`/admin/deliveries/${order_id}/assign`, { rider_id })).data,
    autoAssign: async (order_id: string): Promise<Delivery> =>
        (await api.post(`/admin/deliveries/${order_id}/auto-assign`)).data,
    analytics: async () => (await api.get("/admin/riders/analytics/summary")).data,
    deliveries: async (params: { status?: string; rider?: string; page?: number; page_size?: number } = {}) =>
        (await api.get<{ items: Delivery[]; total: number; page: number; page_size: number }>(
            "/admin/deliveries",
            { params },
        )).data,
};
